import { useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { api } from "../lib/api";
import { trackLead } from "../lib/ga";
import { SERVICES } from "../data/site";
import { CITIES } from "../data/cities";
import { Reveal } from "./Reveal";

const BIEN_OPTIONS = ["Maison", "Appartement", "Local commercial", "Autre"];
const EASE = [0.16, 1, 0.3, 1];

async function compressImage(file) {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
            const img = new Image();
            img.onload = () => {
                const canvas = document.createElement("canvas");
                const scale = Math.min(1, 1280 / Math.max(img.width, img.height));
                canvas.width = Math.round(img.width * scale);
                canvas.height = Math.round(img.height * scale);
                canvas.getContext("2d").drawImage(img, 0, 0, canvas.width, canvas.height);
                resolve(canvas.toDataURL("image/jpeg", 0.72));
            };
            img.onerror = reject;
            img.src = reader.result;
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
    });
}

export default function QuoteForm() {
    const [step, setStep] = useState(0);
    const [data, setData] = useState({
        typeProjet: "", typeBien: "", surface: "", ville: "",
        nom: "", email: "", telephone: "", message: "", photos: [],
    });
    const [ref, setRef] = useState(null);
    const [sending, setSending] = useState(false);
    const fileRef = useRef(null);

    const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

    const steps = ["Projet", "Surface & photos", "Coordonnées", "Récapitulatif"];

    const canNext = () => {
        if (step === 0) return data.typeProjet && data.typeBien;
        if (step === 1) return data.ville.trim().length >= 2;
        if (step === 2)
            return (
                data.nom.trim().length >= 2 &&
                /^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(data.email) &&
                data.telephone.replace(/\D/g, "").length >= 8
            );
        return true;
    };

    const onFiles = async (e) => {
        const files = Array.from(e.target.files || []).slice(0, 3 - data.photos.length);
        const compressed = [];
        for (const f of files) {
            if (!f.type.startsWith("image/")) continue;
            try {
                compressed.push(await compressImage(f));
            } catch {
                toast.error("Impossible de lire l'image " + f.name);
            }
        }
        setData((d) => ({ ...d, photos: [...d.photos, ...compressed].slice(0, 3) }));
        if (fileRef.current) fileRef.current.value = "";
    };

    const submit = async () => {
        setSending(true);
        try {
            const payload = {
                ...data,
                surface: data.surface ? parseFloat(data.surface) : null,
                typeBien: data.typeBien || null,
            };
            const res = await api.post("/devis", payload);
            setRef(res.data.ref);
            trackLead({ project_type: data.typeProjet, city: data.ville });
            toast.success("Votre demande a bien été envoyée");
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Erreur d'envoi — vérifiez votre connexion ou appelez-nous");
        } finally {
            setSending(false);
        }
    };

    if (ref) {
        return (
            <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, ease: EASE }}
                className="max-w-2xl mx-auto text-center border border-gold/40 bg-coal p-10 sm:p-14"
                data-testid="devis-success"
            >
                <div className="w-14 h-14 mx-auto rounded-full bg-gold text-ink flex items-center justify-center text-2xl font-bold">✓</div>
                <h2 className="mt-8 text-3xl sm:text-4xl font-light tracking-tight text-ivory">
                    Demande envoyée, <span className="font-display italic text-gold">merci !</span>
                </h2>
                <p className="mt-4 text-smoke leading-relaxed">
                    Votre référence de suivi : <strong className="text-gold tracking-widest">{ref}</strong>
                </p>
                <p className="mt-2 text-smoke leading-relaxed">
                    Un accusé de réception vient de partir dans votre boîte mail, et nous
                    revenons vers vous <strong className="text-ivory">sous 48 h ouvrées</strong>.
                </p>
            </motion.div>
        );
    }

    return (
        <div className="max-w-3xl mx-auto" data-testid="quote-form">
            <div className="flex items-center gap-2 mb-10" data-testid="devis-progress">
                {steps.map((s, i) => (
                    <div key={s} className="flex-1">
                        <div
                            className={`h-[3px] transition-colors duration-500 ${i <= step ? "bg-gold" : "bg-white/10"}`}
                        />
                        <p className={`mt-2 text-[10px] uppercase tracking-[0.2em] ${i === step ? "text-gold" : "text-smoke/60"}`}>
                            {i + 1}. {s}
                        </p>
                    </div>
                ))}
            </div>

            <AnimatePresence mode="wait">
                <motion.div
                    key={step}
                    initial={{ opacity: 0, x: 32 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: -32 }}
                    transition={{ duration: 0.45, ease: EASE }}
                    className="border border-linew bg-coal p-7 sm:p-10"
                >
                    {step === 0 && (
                        <div data-testid="devis-step-0">
                            <h2 className="text-2xl sm:text-3xl font-light text-ivory">Quel est votre projet ?</h2>
                            <div className="mt-8 grid sm:grid-cols-2 gap-3">
                                {SERVICES.map((s) => (
                                    <button
                                        key={s.slug}
                                        onClick={() => set("typeProjet", s.title)}
                                        className={`text-left border p-4 transition-all duration-300 ${
                                            data.typeProjet === s.title
                                                ? "border-gold bg-gold/10 text-ivory"
                                                : "border-linew text-ivory/75 hover:border-gold/50"
                                        }`}
                                        data-testid={`devis-projet-${s.slug}`}
                                    >
                                        <span className="font-semibold text-sm">{s.title}</span>
                                        <span className="block text-xs text-smoke mt-1">{s.short.slice(0, 64)}…</span>
                                    </button>
                                ))}
                            </div>
                            <p className="mt-8 text-[11px] uppercase tracking-[0.25em] text-gold mb-3">Type de bien</p>
                            <div className="flex flex-wrap gap-2">
                                {BIEN_OPTIONS.map((b) => (
                                    <button
                                        key={b}
                                        onClick={() => set("typeBien", b)}
                                        className={`rounded-full px-5 py-2.5 text-sm border transition-all duration-300 ${
                                            data.typeBien === b
                                                ? "bg-gold text-ink border-gold font-bold"
                                                : "border-linew text-ivory/75 hover:border-gold/50"
                                        }`}
                                        data-testid={`devis-bien-${b.toLowerCase().replace(/\s/g, "-")}`}
                                    >
                                        {b}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {step === 1 && (
                        <div data-testid="devis-step-1">
                            <h2 className="text-2xl sm:text-3xl font-light text-ivory">Surface &amp; localisation</h2>
                            <div className="mt-8 grid sm:grid-cols-2 gap-6">
                                <label className="block">
                                    <span className="text-[11px] uppercase tracking-[0.25em] text-gold">Surface estimée (m²)</span>
                                    <input
                                        type="number"
                                        min="1"
                                        value={data.surface}
                                        onChange={(e) => set("surface", e.target.value)}
                                        placeholder="Ex : 24"
                                        className="mt-2 w-full bg-ink border border-linew focus:border-gold outline-none px-4 py-3.5 text-ivory transition-colors"
                                        data-testid="devis-surface-input"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-[11px] uppercase tracking-[0.25em] text-gold">Ville du chantier *</span>
                                    <input
                                        type="text"
                                        value={data.ville}
                                        onChange={(e) => set("ville", e.target.value)}
                                        placeholder="Ex : Chaumont-en-Vexin"
                                        list="villes-liste"
                                        className="mt-2 w-full bg-ink border border-linew focus:border-gold outline-none px-4 py-3.5 text-ivory transition-colors"
                                        data-testid="devis-ville-input"
                                    />
                                    <datalist id="villes-liste">
                                        {CITIES.map((c) => (
                                            <option key={c.slug} value={c.name} />
                                        ))}
                                    </datalist>
                                </label>
                            </div>

                            <div className="mt-8">
                                <p className="text-[11px] uppercase tracking-[0.25em] text-gold mb-3">
                                    Photos du chantier (jusqu'à 3 — fortement recommandé)
                                </p>
                                <input
                                    ref={fileRef}
                                    type="file"
                                    accept="image/*"
                                    multiple
                                    onChange={onFiles}
                                    className="hidden"
                                    id="devis-photos"
                                    data-testid="devis-photos-input"
                                />
                                <label
                                    htmlFor="devis-photos"
                                    className={`block border border-dashed px-4 py-8 text-center cursor-pointer transition-colors ${
                                        data.photos.length >= 3 ? "opacity-40 pointer-events-none border-white/20" : "border-gold/50 hover:bg-gold/5"
                                    }`}
                                    data-testid="devis-photos-dropzone"
                                >
                                    <span className="text-sm text-ivory/70">
                                        {data.photos.length >= 3
                                            ? "3 photos maximum atteint"
                                            : "Cliquez pour ajouter des photos — elles nous permettent un chiffrage plus précis"}
                                    </span>
                                </label>
                                {data.photos.length > 0 && (
                                    <div className="mt-4 flex gap-3" data-testid="devis-photos-preview">
                                        {data.photos.map((p, i) => (
                                            <div key={i} className="relative w-20 h-20 border border-linew">
                                                <img src={p} alt={`Photo ${i + 1}`} className="w-full h-full object-cover" />
                                                <button
                                                    onClick={() => setData((d) => ({ ...d, photos: d.photos.filter((_, j) => j !== i) }))}
                                                    className="absolute -top-2 -right-2 w-6 h-6 rounded-full bg-gold text-ink text-xs font-bold"
                                                    aria-label="Retirer la photo"
                                                    data-testid={`devis-photo-remove-${i}`}
                                                >
                                                    ×
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    {step === 2 && (
                        <div data-testid="devis-step-2">
                            <h2 className="text-2xl sm:text-3xl font-light text-ivory">Vos coordonnées</h2>
                            <div className="mt-8 grid sm:grid-cols-2 gap-6">
                                <label className="block sm:col-span-2">
                                    <span className="text-[11px] uppercase tracking-[0.25em] text-gold">Nom et prénom *</span>
                                    <input
                                        type="text"
                                        value={data.nom}
                                        onChange={(e) => set("nom", e.target.value)}
                                        placeholder="Ex : Marie Dupont"
                                        className="mt-2 w-full bg-ink border border-linew focus:border-gold outline-none px-4 py-3.5 text-ivory transition-colors"
                                        data-testid="devis-nom-input"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-[11px] uppercase tracking-[0.25em] text-gold">Email *</span>
                                    <input
                                        type="email"
                                        value={data.email}
                                        onChange={(e) => set("email", e.target.value)}
                                        placeholder="marie@exemple.fr"
                                        className="mt-2 w-full bg-ink border border-linew focus:border-gold outline-none px-4 py-3.5 text-ivory transition-colors"
                                        data-testid="devis-email-input"
                                    />
                                </label>
                                <label className="block">
                                    <span className="text-[11px] uppercase tracking-[0.25em] text-gold">Téléphone *</span>
                                    <input
                                        type="tel"
                                        value={data.telephone}
                                        onChange={(e) => set("telephone", e.target.value)}
                                        placeholder="06 12 34 56 78"
                                        className="mt-2 w-full bg-ink border border-linew focus:border-gold outline-none px-4 py-3.5 text-ivory transition-colors"
                                        data-testid="devis-telephone-input"
                                    />
                                </label>
                                <label className="block sm:col-span-2">
                                    <span className="text-[11px] uppercase tracking-[0.25em] text-gold">Détails du projet (optionnel)</span>
                                    <textarea
                                        value={data.message}
                                        onChange={(e) => set("message", e.target.value)}
                                        rows="4"
                                        placeholder="Précisez vos envies, vos délais, les pièces concernées…"
                                        className="mt-2 w-full bg-ink border border-linew focus:border-gold outline-none px-4 py-3.5 text-ivory transition-colors resize-none"
                                        data-testid="devis-message-input"
                                    />
                                </label>
                            </div>
                        </div>
                    )}

                    {step === 3 && (
                        <div data-testid="devis-step-3">
                            <h2 className="text-2xl sm:text-3xl font-light text-ivory">Récapitulatif</h2>
                            <dl className="mt-8 divide-y divide-white/5 border-y border-white/5">
                                {[
                                    ["Projet", data.typeProjet],
                                    ["Type de bien", data.typeBien],
                                    ["Surface", data.surface ? `${data.surface} m²` : "—"],
                                    ["Ville", data.ville],
                                    ["Nom", data.nom],
                                    ["Email", data.email],
                                    ["Téléphone", data.telephone],
                                    ["Message", data.message || "—"],
                                    ["Photos", data.photos.length ? `${data.photos.length} photo(s)` : "aucune"],
                                ].map(([k, v]) => (
                                    <div key={k} className="flex justify-between gap-6 py-3">
                                        <dt className="text-[11px] uppercase tracking-[0.2em] text-gold pt-1">{k}</dt>
                                        <dd className="text-ivory/85 text-sm text-right">{v}</dd>
                                    </div>
                                ))}
                            </dl>
                            <p className="mt-6 text-xs text-smoke leading-relaxed">
                                En envoyant ce formulaire, vous acceptez d'être recontacté au sujet de votre demande de devis.
                                Vos données ne sont jamais partagées avec des tiers.
                            </p>
                        </div>
                    )}
                </motion.div>
            </AnimatePresence>

            <div className="mt-6 flex items-center justify-between gap-4">
                <button
                    onClick={() => setStep((s) => Math.max(0, s - 1))}
                    disabled={step === 0}
                    className={`text-sm uppercase tracking-[0.2em] py-3 px-6 transition-colors ${
                        step === 0 ? "text-smoke/40 cursor-not-allowed" : "text-smoke hover:text-gold"
                    }`}
                    data-testid="devis-back-button"
                >
                    ← Retour
                </button>
                {step < 3 ? (
                    <button
                        onClick={() => canNext() && setStep((s) => s + 1)}
                        disabled={!canNext()}
                        className={`rounded-full px-10 py-4 font-bold text-sm transition-all duration-300 ${
                            canNext()
                                ? "bg-gold text-ink hover:scale-[1.04]"
                                : "bg-white/10 text-smoke/50 cursor-not-allowed"
                        }`}
                        data-testid="devis-next-button"
                    >
                        {step === 2 ? "Vérifier ma demande" : "Continuer"}
                    </button>
                ) : (
                    <button
                        onClick={submit}
                        disabled={sending}
                        className="rounded-full bg-gold text-ink font-bold px-10 py-4 hover:scale-[1.04] transition-transform duration-300 disabled:opacity-60"
                        data-testid="devis-submit-button"
                    >
                        {sending ? "Envoi en cours…" : "Envoyer ma demande"}
                    </button>
                )}
            </div>
        </div>
    );
}
